import { useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { InputSelect, Notification } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { ERROR, LENGTH_CC_NUMBER } from './LinkagePay.constants';
import { L10N } from './LinkagePay.l10n';
import { VomsisService } from './services';
import { trackError } from '../../../../../helpers/trackError';
import { EVENT } from '../../../ButtonPayment/ButtonPayment.constants';
import { Card } from '../Card/Card';

const LinkagePay = ({ accessToken, currency, value, onChange = () => {}, onError = () => {}, ...others }) => {
  const { currencyFormat, translate } = useLocale();
  const { value: { payment = {} } = {}, value: storeValue, set } = useStore();

  const [form, setForm] = useState({});
  const [formError, setFormError] = useState({});
  const [installment, setInstallment] = useState();
  const [installments, setInstallments] = useState();
  const [responseError, setResponseError] = useState();

  useEffect(() => {
    document.addEventListener(EVENT.SERVICE_RESPONSE, handleResponse);

    return () => document.removeEventListener(EVENT.SERVICE_RESPONSE, handleResponse);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, formError, installment, responseError, storeValue]);

  useEffect(() => {
    if (!responseError) return;

    if (responseError)
      trackError('LinkagePay', responseError instanceof Error ? responseError.stack : responseError?.message);

    set({ payment: { ...payment, error: ERROR } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseError]);

  const handleResponse = async ({
    detail: { configuration: { notificationUrl, postObject: data = {} } = {} } = {},
  }) => {
    if (Object.entries(formError).filter(([, value]) => value !== undefined).length > 0) return;

    setResponseError();

    const [month, year] = form.expire.split('/');
    const response = await VomsisService.createPayment(accessToken, {
      ...data,
      ...(installment ? { installment: parseInt(installment) } : undefined),
      creditCardHolderName: form.name,
      creditCardPan: form.card?.trim()?.replace(/\s/g, ''),
      creditCardExpiryMonth: month.padStart(2, '0'),
      creditCardExpiryYear: `20${year}`,
      creditCardCvc: form.CVV,
    }).catch(async (error) => {
      setResponseError(error);
      await VomsisService.notificationError(error, notificationUrl);
    });

    if (response) {
      window.location.href = URL.createObjectURL(new Blob([response], { type: 'text/html' }));
    }
  };

  const handleChange = (values) => {
    const card = values.card?.replace(/\s/g, '') || '';
    if (
      card.length >= LENGTH_CC_NUMBER &&
      (!installments ||
        (form.card?.length >= LENGTH_CC_NUMBER &&
          values.card.substring(0, LENGTH_CC_NUMBER) !== form.card.substring(0, LENGTH_CC_NUMBER)))
    ) {
      (async () => {
        const response = (await VomsisService.getInstallments(accessToken, card).catch()) || [];
        const [firstInstallment] = response;

        setInstallment(firstInstallment.installment.toString());
        response.length > 1 && firstInstallment.currency === currency && setInstallments(response);
      })();
    } else if (card.length < LENGTH_CC_NUMBER) {
      setInstallments();
    }

    setForm(values);

    const { type } = values;

    onChange({ ...payment, token: accessToken, type });
  };

  const handleError = (error) => {
    setFormError(error);
    onError(error);
  };

  return (
    <>
      <Card {...others} cards={undefined} showCVV onChange={handleChange} onError={handleError} />
      {installments?.length > 0 && (
        <InputSelect
          id="installments"
          label={translate(L10N.LABEL_INSTALLMENTS)}
          options={installments.map(
            ({ amount, currency, installment, ratio } = {}) =>
              `${amount} x ${currencyFormat({
                value: (value * (ratio / 100 + 1)) / installment,
                currency,
              })} = ${currencyFormat({ value: value * (ratio / 100 + 1), currency })}`,
          )}
          value={installment}
          values={installments.map(({ installment } = {}) => installment)}
          onChange={setInstallment}
        />
      )}
      {responseError && (
        <Notification error small wide>
          {translate(L10N.NOTIFICATION_ERROR_LINKAGE_PAY)}
        </Notification>
      )}
    </>
  );
};

LinkagePay.displayName = 'Mirai:Payments:LinkagePay';

LinkagePay.propTypes = {
  accessToken: PropTypes.string,
  currency: PropTypes.string,
  value: PropTypes.number,
  onChange: PropTypes.func,
  onError: PropTypes.func,
};

export { LinkagePay };
